import { useL } from "@/lib/useL";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ActionBarButton } from "@/components/ActionBar";

const HELP = <><b>Cartes de révision</b> : SUX1 transforme ce résumé en 5, 10 ou 20 cartes question / réponse (1 crédit), ou en « Toutes » : autant de cartes que le texte d'origine le permet, jusqu'à 50 (2 crédits). Retournez chaque carte, puis dites « Je savais » ou « À revoir ». SUX1 vous les représente au bon moment : demain, dans 3 jours, puis dans 1 semaine. Impression PDF à découper et partage avec la classe par lien ou QR code. Vous pourrez ajouter 10 cartes de plus à tout moment (1 crédit).</>;

// Creates a 10-card flashcard deck from a saved summary (1 credit), then opens it.
const HELP_EN = <><b>Flashcards</b>: SUX1 turns this summary into 5, 10 or 20 question / answer cards (1 credit), or « All »: as many cards as the source allows, up to 50 (2 credits). Flip each card, then say « I knew it » or « Review again ». SUX1 brings them back at the right time: tomorrow, in 3 days, then in 1 week. Cut-out PDF printing and class sharing by link or QR code. Add 10 more cards at any time (1 credit).</>;

export function FlashcardsButton({ summaryId }) {
  const L = useL();
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(10);
  const cost = count === 0 ? 2 : 1;
  const navigate = useNavigate();
  if (!summaryId) return null;
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/flashcards", { summary_id: summaryId, count });
      toast.success(L(`${data.total} cartes de révision créées.`, `${data.total} flashcards created.`));
      navigate(`/cartes/${data.id}`);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 402) toast.error(L(`Il vous faut ${cost} crédit(s) pour créer les cartes.`, `You need ${cost} credit(s) to create the cards.`), { action: { label: L("Recharger", "Top up"), onClick: () => navigate("/billing/upgrade") } });
      else if (st === 401) toast.error(L("Créez un compte gratuit pour faire des cartes de révision.", "Create a free account to make flashcards."));
      else toast.error(err?.response?.data?.detail || L("La création des cartes a échoué.", "Flashcard creation failed."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-8" data-testid="flashcards-create">
      <ActionBarButton icon={busy ? Loader2 : Layers} label={busy ? L("Création des cartes…", "Creating cards…") : L("Cartes de révision", "Flashcards")} onClick={create}
        disabled={busy} chevron={false} testid="flashcards-create-btn" help={L(HELP, HELP_EN)} className="sm:min-h-[4.5rem]"
        tip={L("Cartes de révision : transforme ce résumé en cartes question / réponse à réviser au bon moment.", "Flashcards: turns this summary into question / answer cards to review at the right time.")} />
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[11px] text-muted-foreground">
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} disabled={busy} data-testid="flashcards-count-select"
          aria-label={L("Nombre de cartes", "Number of cards")} className="border border-[#002FA7] bg-background text-[#002FA7] font-mono text-[11px] px-2 py-1">
          <option value={5}>{L("5 cartes", "5 cards")}</option>
          <option value={10}>{L("10 cartes", "10 cards")}</option>
          <option value={20}>{L("20 cartes", "20 cards")}</option>
          <option value={0}>{L("Toutes (jusqu'à 50)", "All (up to 50)")}</option>
        </select>
        <span data-testid="flashcards-cost">{L("question / réponse, révision espacée", "question / answer, spaced repetition")} · <b>{cost} {L("crédit", "credit")}{cost > 1 ? "s" : ""}</b></span>
      </div>
    </div>
  );
}

export default FlashcardsButton;
